import { useState } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { stats, siteInfo } from '@/mocks/comics';

const milestones = [
  { year: '2020', title: '平台成立', desc: '尾巴漫画网正式上线，专注韩国漫画在线阅读服务，首批上线500部优质韩漫。' },
  { year: '2021', title: '快速发展', desc: '漫画资源突破2000部，日均访问量突破10万，成为韩漫阅读领域的黑马平台。' },
  { year: '2022', title: '技术升级', desc: '推出新一代下拉式阅读器，优化移动端体验，用户满意度大幅提升至95%以上。' },
  { year: '2023', title: '生态扩展', desc: '漫画总数突破5000部，建立完善的分类体系和推荐算法，覆盖全类型韩漫。' },
  { year: '2024', title: '品牌升级', desc: '完成品牌视觉升级，推出社区评论功能，读者互动体验全面提升。' },
  { year: '2025', title: '行业领先', desc: '漫画总数突破8000部，日均访问突破30万，成为韩漫在线阅读领域的标杆平台。' },
];

const faqs = [
  {
    q: '尾巴漫画网的漫画资源是从哪里来的？',
    a: '尾巴漫画网与多家韩国漫画版权方建立了合作关系，所有漫画内容均获得正版授权或遵循相关版权规范。我们致力于为读者提供合法合规的韩漫阅读体验。',
  },
  {
    q: '网站上的漫画需要付费吗？',
    a: '尾巴漫画网目前提供免费在线阅读服务，读者无需付费即可畅读平台上的海量韩漫资源。我们通过广告合作和品牌推广维持平台的运营。',
  },
  {
    q: '尾巴漫画网支持哪些设备阅读？',
    a: '我们的网站采用响应式设计，完美适配电脑、平板和手机等各种设备。无论在哪个平台，都能享受流畅的下拉式阅读体验。手机端阅读体验尤为出色。',
  },
  {
    q: '漫画更新频率是怎样的？',
    a: '尾巴漫画网每日更新大量热门连载漫画，热门作品通常每周更新1-3话。您可以通过订阅通知功能，第一时间获取最新的更新信息。',
  },
  {
    q: '如何反馈漫画问题或提供建议？',
    a: '您可以通过页面底部的商务合作表单提交反馈，或发送邮件至我们的官方邮箱。我们非常重视每一位读者的意见，会及时处理您的反馈。',
  },
];

export default function AboutPage() {
  const [openFaq, setOpenFaq] = useState<number | null>(null);
  const [formStatus, setFormStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const honeypot = (form.elements.namedItem('phone_alt') as HTMLInputElement)?.value?.trim();
    if (honeypot) {
      setFormStatus('success');
      (form.querySelector('textarea[name="message"]') as HTMLTextAreaElement).value = '';
      return;
    }

    setFormStatus('loading');
    setFormError('');

    try {
      const formData = new FormData(form);
      const response = await fetch('http://sansevieria.cn/', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });
      const responseText = await response.text();
      let parsed;
      try { parsed = JSON.parse(responseText); } catch { parsed = {}; }

      if (response.ok && parsed?.code === 'OK') {
        setFormStatus('success');
        (form.querySelector('textarea[name="message"]') as HTMLTextAreaElement).value = '';
        (form.querySelector('input[name="name"]') as HTMLInputElement).value = '';
        (form.querySelector('input[name="email"]') as HTMLInputElement).value = '';
      } else {
        const serverMsg = parsed?.meta?.message || parsed?.message || responseText;
        if (serverMsg && serverMsg.includes('spam')) {
          setFormStatus('success');
          return;
        }
        setFormStatus('error');
        setFormError(serverMsg || '提交失败，请稍后重试');
      }
    } catch {
      setFormStatus('error');
      setFormError('网络错误，请稍后重试');
    }
  };

  return (
    <>
      <Navbar />
      <main>
        {/* Breadcrumb */}
        <section className="bg-background-950 pt-24 pb-10 md:pt-32 md:pb-14">
          <div className="w-full px-4 md:px-6">
            <nav className="flex items-center gap-2 text-xs text-foreground-400 mb-4">
              <Link to="/" className="hover:text-primary-300 transition-colors cursor-pointer whitespace-nowrap">
                首页
              </Link>
              <span className="w-3 h-3 flex items-center justify-center">
                <i className="ri-arrow-right-s-line text-xs"></i>
              </span>
              <span className="text-primary-300 whitespace-nowrap">关于我们</span>
            </nav>
            <h1 className="font-heading text-3xl md:text-5xl font-bold text-background-50">
              关于尾巴漫画
            </h1>
            <p className="text-foreground-400 text-sm mt-3 max-w-xl leading-relaxed">
              专注韩国漫画在线阅读，致力于为每一位韩漫爱好者提供极致的阅读体验
            </p>
          </div>
        </section>

        {/* Brand Story */}
        <section className="bg-background-50 py-16 md:py-24">
          <div className="w-full px-4 md:px-6">
            <div className="flex flex-col lg:flex-row gap-12 lg:gap-20 items-center">
              <div className="lg:w-1/2">
                <span className="inline-block px-4 py-1.5 bg-primary-100/70 text-primary-700 text-xs font-medium rounded-full mb-4">
                  品牌故事
                </span>
                <h2 className="font-heading text-3xl md:text-4xl font-bold text-foreground-950 leading-tight mb-6">
                  从热爱出发，<br />打造极致的韩漫阅读平台
                </h2>
                <p className="text-foreground-600 text-sm leading-relaxed mb-4">
                  尾巴漫画网成立于2020年，由一群热爱韩国漫画的年轻人共同创立。我们深知每一位漫画爱好者的需求——高清画质、流畅阅读、丰富资源以及无广告干扰的环境。基于这份热爱和理解，我们打造了这个专注于韩漫的在线阅读平台。
                </p>
                <p className="text-foreground-600 text-sm leading-relaxed mb-4">
                  我们坚持"内容为王，体验至上"的理念。每一部漫画都经过精心筛选和优化，确保画质清晰、排版合理。下拉式阅读设计让读者无需繁琐的翻页操作，只需轻轻滑动，就能沉浸于精彩的漫画世界中。
                </p>
                <p className="text-foreground-600 text-sm leading-relaxed">
                  从创站之初的500部漫画，到今天超过8,500部优质韩漫资源，我们始终保持着对漫画的热爱和对品质的追求。未来，我们将继续拓展漫画资源库，提升阅读体验，让更多人爱上韩国漫画。
                </p>
              </div>
              <div className="lg:w-1/2">
                <div className="aspect-[4/3] rounded-2xl overflow-hidden bg-background-200">
                  <img
                    src="https://readdy.ai/api/search-image?query=cozy%20modern%20reading%20room%20with%20tablet%20displaying%20colorful%20Korean%20webtoon%2C%20warm%20ambient%20lighting%2C%20bookshelves%20in%20background%2C%20comfortable%20sofa%2C%20plants%20decoration%2C%20relaxed%20atmosphere%2C%20clean%20minimalist%20interior%20design%2C%20lifestyle%20photography&width=1200&height=900&seq=about-brand-story&orientation=landscape"
                    alt="尾巴漫画阅读场景"
                    className="w-full h-full object-cover"
                  />
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Platform Stats */}
        <section className="bg-background-100 py-16 md:py-20">
          <div className="w-full px-4 md:px-6">
            <div className="text-center mb-14">
              <span className="inline-block px-4 py-1.5 bg-secondary-100 text-secondary-700 text-xs font-medium rounded-full">
                平台数据
              </span>
              <h2 className="font-heading text-3xl md:text-4xl font-bold text-foreground-950 mt-4">
                我们用数据说话
              </h2>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
              <div className="bg-background-50 rounded-2xl p-6 md:p-8 text-center border border-background-200/70">
                <span className="font-heading text-4xl md:text-5xl font-black text-primary-500">
                  {stats.totalComics.toLocaleString()}+
                </span>
                <p className="text-foreground-600 text-sm mt-3 font-medium">漫画总数</p>
                <p className="text-foreground-400 text-xs mt-1">涵盖多种类型韩漫</p>
              </div>
              <div className="bg-background-50 rounded-2xl p-6 md:p-8 text-center border border-background-200/70">
                <span className="font-heading text-4xl md:text-5xl font-black text-accent-500">
                  {(stats.totalChapters / 10000).toFixed(1)}万+
                </span>
                <p className="text-foreground-600 text-sm mt-3 font-medium">总章节数</p>
                <p className="text-foreground-400 text-xs mt-1">海量内容持续更新</p>
              </div>
              <div className="bg-background-50 rounded-2xl p-6 md:p-8 text-center border border-background-200/70">
                <span className="font-heading text-4xl md:text-5xl font-black text-secondary-500">
                  {(stats.dailyVisitors / 10000).toFixed(0)}万+
                </span>
                <p className="text-foreground-600 text-sm mt-3 font-medium">日均访问</p>
                <p className="text-foreground-400 text-xs mt-1">忠实读者群体</p>
              </div>
              <div className="bg-background-50 rounded-2xl p-6 md:p-8 text-center border border-background-200/70">
                <span className="font-heading text-4xl md:text-5xl font-black text-primary-500">
                  {stats.satisfaction}%
                </span>
                <p className="text-foreground-600 text-sm mt-3 font-medium">用户满意度</p>
                <p className="text-foreground-400 text-xs mt-1">高品质阅读体验</p>
              </div>
            </div>
          </div>
        </section>

        {/* Milestones Timeline */}
        <section className="bg-background-50 py-16 md:py-24">
          <div className="w-full px-4 md:px-6">
            <div className="max-w-xl mb-14">
              <span className="inline-block px-4 py-1.5 bg-accent-100 text-accent-700 text-xs font-medium rounded-full">
                发展历程
              </span>
              <h2 className="font-heading text-3xl md:text-4xl font-bold text-foreground-950 mt-4">
                我们的成长足迹
              </h2>
            </div>
            <div className="relative max-w-3xl mx-auto">
              <div className="absolute left-4 md:left-1/2 top-0 bottom-0 w-px bg-background-200/70 md:-translate-x-px"></div>
              {milestones.map((item, idx) => (
                <div
                  key={item.year}
                  className={`relative flex items-start gap-6 md:gap-12 mb-12 ${
                    idx % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'
                  }`}
                >
                  <div className="flex-shrink-0 w-8 h-8 md:w-10 md:h-10 rounded-full bg-primary-500 flex items-center justify-center z-10">
                    <span className="w-3 h-3 md:w-4 md:h-4 rounded-full bg-background-50"></span>
                  </div>
                  <div className={`flex-1 ${idx % 2 === 0 ? 'md:text-right md:pr-12' : 'md:text-left md:pl-12'}`}>
                    <span className="font-heading text-xl font-bold text-primary-500">{item.year}</span>
                    <h3 className="font-heading text-lg font-bold text-foreground-950 mt-1">{item.title}</h3>
                    <p className="text-foreground-600 text-sm mt-2 leading-relaxed">{item.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Contact Form */}
        <section id="contact" className="bg-background-100 py-16 md:py-24">
          <div className="w-full px-4 md:px-6">
            <div className="flex flex-col lg:flex-row gap-12 lg:gap-20">
              <div className="lg:w-1/2">
                <span className="inline-block px-4 py-1.5 bg-primary-100/70 text-primary-700 text-xs font-medium rounded-full mb-4">
                  联系我们
                </span>
                <h2 className="font-heading text-3xl md:text-4xl font-bold text-foreground-950 leading-tight mb-6">
                  商务合作<br />与内容反馈
                </h2>
                <p className="text-foreground-600 text-sm leading-relaxed mb-8">
                  无论您是漫画版权方、广告合作商，还是想为平台提出建议的读者，我们都非常欢迎您的联系。请填写右侧表单，我们会尽快回复您。
                </p>
                <div className="flex flex-col gap-4">
                  <div className="flex items-center gap-4">
                    <div className="w-11 h-11 rounded-xl bg-primary-100 flex items-center justify-center flex-shrink-0">
                      <span className="w-5 h-5 flex items-center justify-center">
                        <i className="ri-mail-line text-lg text-primary-600"></i>
                      </span>
                    </div>
                    <div>
                      <p className="text-foreground-500 text-xs">电子邮箱</p>
                      <p className="text-foreground-900 text-sm font-medium">{siteInfo.email}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-4">
                    <div className="w-11 h-11 rounded-xl bg-primary-100 flex items-center justify-center flex-shrink-0">
                      <span className="w-5 h-5 flex items-center justify-center">
                        <i className="ri-time-line text-lg text-primary-600"></i>
                      </span>
                    </div>
                    <div>
                      <p className="text-foreground-500 text-xs">工作时间</p>
                      <p className="text-foreground-900 text-sm font-medium">周一至周五 9:00 - 18:00</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-4">
                    <div className="w-11 h-11 rounded-xl bg-primary-100 flex items-center justify-center flex-shrink-0">
                      <span className="w-5 h-5 flex items-center justify-center">
                        <i className="ri-map-pin-line text-lg text-primary-600"></i>
                      </span>
                    </div>
                    <div>
                      <p className="text-foreground-500 text-xs">平台性质</p>
                      <p className="text-foreground-900 text-sm font-medium">{siteInfo.address}</p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="lg:w-1/2">
                <div className="bg-background-50 rounded-2xl p-6 md:p-8 border border-background-200/70">
                  <h3 className="font-heading text-xl font-bold text-foreground-950 mb-6">发送消息</h3>
                  <form id="about-contact-form" onSubmit={handleSubmit} className="flex flex-col gap-4">
                    <div>
                      <label htmlFor="contact-name" className="block text-foreground-700 text-sm font-medium mb-1.5">
                        姓名 <span className="text-primary-500">*</span>
                      </label>
                      <input
                        type="text"
                        id="contact-name"
                        name="name"
                        required
                        placeholder="请输入您的姓名"
                        className="w-full px-4 py-2.5 bg-background-100 border border-background-200/70 rounded-xl text-sm text-foreground-900 placeholder-foreground-400 outline-none focus:border-primary-400 transition-colors"
                      />
                    </div>
                    <div>
                      <label htmlFor="contact-email" className="block text-foreground-700 text-sm font-medium mb-1.5">
                        邮箱 <span className="text-primary-500">*</span>
                      </label>
                      <input
                        type="email"
                        id="contact-email"
                        name="email"
                        required
                        placeholder="请输入您的邮箱"
                        className="w-full px-4 py-2.5 bg-background-100 border border-background-200/70 rounded-xl text-sm text-foreground-900 placeholder-foreground-400 outline-none focus:border-primary-400 transition-colors"
                      />
                    </div>
                    <div>
                      <label htmlFor="contact-subject" className="block text-foreground-700 text-sm font-medium mb-1.5">
                        主题
                      </label>
                      <input
                        type="text"
                        id="contact-subject"
                        name="subject"
                        placeholder="请选择或输入主题"
                        className="w-full px-4 py-2.5 bg-background-100 border border-background-200/70 rounded-xl text-sm text-foreground-900 placeholder-foreground-400 outline-none focus:border-primary-400 transition-colors"
                      />
                    </div>
                    <div>
                      <label htmlFor="contact-message" className="block text-foreground-700 text-sm font-medium mb-1.5">
                        消息内容 <span className="text-primary-500">*</span>
                      </label>
                      <textarea
                        id="contact-message"
                        name="message"
                        required
                        maxLength={500}
                        rows={4}
                        placeholder="请输入您的消息内容（最多500字）"
                        className="w-full px-4 py-2.5 bg-background-100 border border-background-200/70 rounded-xl text-sm text-foreground-900 placeholder-foreground-400 outline-none focus:border-primary-400 transition-colors resize-none"
                      ></textarea>
                      <p className="text-foreground-400 text-xs mt-1">最多500个字符</p>
                    </div>
                    <input
                      type="text"
                      name="phone_alt"
                      tabIndex={-1}
                      autoComplete="off"
                      aria-hidden="true"
                      readOnly
                      className="absolute opacity-0 pointer-events-none"
                      style={{ position: 'absolute', left: '-9999px' }}
                    />
                    <button
                      type="submit"
                      disabled={formStatus === 'loading'}
                      className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-primary-500 text-background-50 rounded-full text-sm font-semibold hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap disabled:opacity-60"
                    >
                      {formStatus === 'loading' ? (
                        <span>发送中...</span>
                      ) : (
                        <>
                          发送消息
                          <span className="w-4 h-4 flex items-center justify-center">
                            <i className="ri-send-plane-line"></i>
                          </span>
                        </>
                      )}
                    </button>
                    {formStatus === 'success' && (
                      <p className="text-primary-600 text-sm">消息发送成功！我们会尽快与您联系。</p>
                    )}
                    {formStatus === 'error' && (
                      <p className="text-red-500 text-sm">{formError || '发送失败，请稍后重试'}</p>
                    )}
                  </form>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section className="bg-background-50 py-16 md:py-24">
          <div className="w-full px-4 md:px-6">
            <div className="max-w-2xl mx-auto">
              <div className="text-center mb-12">
                <span className="inline-block px-4 py-1.5 bg-accent-100 text-accent-700 text-xs font-medium rounded-full">
                  常见问题
                </span>
                <h2 className="font-heading text-3xl md:text-4xl font-bold text-foreground-950 mt-4">
                  您可能想了解
                </h2>
              </div>
              <div className="flex flex-col gap-3">
                {faqs.map((faq, idx) => (
                  <div
                    key={idx}
                    className="bg-background-100 rounded-2xl overflow-hidden border border-background-200/70"
                  >
                    <button
                      onClick={() => setOpenFaq(openFaq === idx ? null : idx)}
                      className="w-full flex items-center justify-between px-5 py-4 text-left cursor-pointer"
                    >
                      <span className="text-foreground-900 text-sm font-semibold pr-4">{faq.q}</span>
                      <span className={`w-6 h-6 flex items-center justify-center flex-shrink-0 transition-transform duration-200 ${openFaq === idx ? 'rotate-45' : ''}`}>
                        <i className="ri-add-line text-lg text-foreground-500"></i>
                      </span>
                    </button>
                    {openFaq === idx && (
                      <div className="px-5 pb-4">
                        <p className="text-foreground-600 text-sm leading-relaxed">{faq.a}</p>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer />

      {/* SEO Structured Data */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'AboutPage',
            name: '关于尾巴漫画 - 尾巴漫画网',
            description: '了解尾巴漫画网——专注韩国漫画在线阅读平台，成立于2020年，提供超过8,500部高清韩漫资源，每日更新。',
            url: 'http://sansevieria.cn/about',
            isPartOf: {
              '@type': 'WebSite',
              name: '尾巴漫画网',
              url: 'http://sansevieria.cn/',
            },
            breadcrumb: {
              '@type': 'BreadcrumbList',
              itemListElement: [
                { '@type': 'ListItem', position: 1, name: '首页', item: 'http://sansevieria.cn/' },
                { '@type': 'ListItem', position: 2, name: '关于我们' },
              ],
            },
            about: {
              '@type': 'Organization',
              name: '尾巴漫画网',
              url: 'http://sansevieria.cn/',
              foundingDate: '2020',
              email: siteInfo.email,
              description: siteInfo.description,
            },
          }),
        }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'FAQPage',
            mainEntity: faqs.map((faq) => ({
              '@type': 'Question',
              name: faq.q,
              acceptedAnswer: {
                '@type': 'Answer',
                text: faq.a,
              },
            })),
          }),
        }}
      />
    </>
  );
}